
import { useState, useCallback } from 'react';
import Button from '../base/Button';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToSection = useCallback((sectionId: string) => {
    requestAnimationFrame(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      setIsMenuOpen(false);
    });
  }, []);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const handleLinkClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    scrollToSection(sectionId);
  }, [scrollToSection]);

  return (
    <header className="header">
      <div className="header-container">
        <div className="logo-container">
          <img 
            src="https://static.readdy.ai/image/bbcc86dc4c3a385b7f9b9f3a7e24f053/bc58823c9490a9cd55c8713379f1f7d6.jfif" 
            alt="tek RVA Logo" 
            className="logo-image"
            loading="eager"
            decoding="sync"
            width="64"
            height="64"
          />
          <span className="logo-text">tek RVA</span>
        </div>

        <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
          <a 
            href="#services"
            onClick={(e) => handleLinkClick(e, 'services')}
            className="text-gray-700 hover:text-blue-600 font-medium cursor-pointer text-sm lg:text-base whitespace-nowrap transition-colors duration-200"
            aria-label="Navigate to Services section"
          >
            Services
          </a>
          <a 
            href="#about"
            onClick={(e) => handleLinkClick(e, 'about')}
            className="text-gray-700 hover:text-blue-600 font-medium cursor-pointer text-sm lg:text-base whitespace-nowrap transition-colors duration-200"
            aria-label="Navigate to About section"
          >
            About
          </a>
          <a 
            href="#client-portal"
            onClick={(e) => handleLinkClick(e, 'client-portal')}
            className="text-gray-700 hover:text-blue-600 font-medium cursor-pointer text-sm lg:text-base whitespace-nowrap transition-colors duration-200"
            aria-label="Navigate to Client Support section"
          >
            Client Support
          </a>
          <a 
            href="#contact"
            onClick={(e) => handleLinkClick(e, 'contact')}
            className="text-gray-700 hover:text-blue-600 font-medium cursor-pointer text-sm lg:text-base whitespace-nowrap transition-colors duration-200"
            aria-label="Navigate to Contact section"
          >
            Contact
          </a>
          <a 
            href="#consultation"
            onClick={(e) => handleLinkClick(e, 'consultation')}
            className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200 whitespace-nowrap text-sm"
            aria-label="Schedule a free consultation"
          >
            Free Consultation
          </a>
        </nav>

        <button 
          className="md:hidden p-2 cursor-pointer w-10 h-10 flex items-center justify-center flex-shrink-0"
          onClick={toggleMenu}
          aria-label={isMenuOpen ? "Close mobile menu" : "Open mobile menu"}
          aria-expanded={isMenuOpen}
        >
          <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl sm:text-2xl text-gray-700 transition-transform duration-200`}></i>
        </button>

        {/* Mobile menu */}
        <div className={`md:hidden absolute top-full left-0 right-0 bg-white shadow-lg overflow-hidden transition-all duration-300 ${isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className={`flex flex-col space-y-4 p-4 transform transition-transform duration-300 ${isMenuOpen ? 'translate-y-0' : '-translate-y-4'}`}>
            <a 
              href="#services"
              onClick={(e) => handleLinkClick(e, 'services')}
              className="text-gray-700 hover:text-blue-600 font-medium text-left cursor-pointer whitespace-nowrap transition-colors duration-200"
              aria-label="Navigate to Services section"
            >
              Services
            </a>
            <a 
              href="#about"
              onClick={(e) => handleLinkClick(e, 'about')}
              className="text-gray-700 hover:text-blue-600 font-medium text-left cursor-pointer whitespace-nowrap transition-colors duration-200"
              aria-label="Navigate to About section"
            >
              About
            </a>
            <a 
              href="#client-portal"
              onClick={(e) => handleLinkClick(e, 'client-portal')}
              className="text-gray-700 hover:text-blue-600 font-medium text-left cursor-pointer whitespace-nowrap transition-colors duration-200"
              aria-label="Navigate to Client Support section"
            >
              Client Support
            </a>
            <a 
              href="#contact"
              onClick={(e) => handleLinkClick(e, 'contact')}
              className="text-gray-700 hover:text-blue-600 font-medium text-left cursor-pointer whitespace-nowrap transition-colors duration-200"
              aria-label="Navigate to Contact section"
            >
              Contact
            </a>
            <a 
              href="#consultation"
              onClick={(e) => handleLinkClick(e, 'consultation')}
              className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200 w-fit whitespace-nowrap text-sm"
              aria-label="Schedule a free consultation"
            >
              Free Consultation
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
